import { HelpCircle, InfoCircle } from "@untitledui/icons";
import {
	type ComponentType,
	createContext,
	type HTMLAttributes,
	type ReactNode,
	type Ref,
	useContext,
} from "react";
import type {
	InputProps as AriaInputProps,
	TextFieldProps as AriaTextFieldProps,
} from "react-aria-components";
import {
	Group as AriaGroup,
	Input as AriaInput,
	TextField as AriaTextField,
} from "react-aria-components";
import type { DisplayFieldFeedback as DisplayFieldFeedbackType } from "@/components/Atoms/DisplayField/display-field.shared";
import { HintText } from "@/components/base/input/hint-text";
import { isRequiredFieldFilled } from "@/components/base/input/field-label";
import { LabelWithFeedback } from "@/components/base/input/label-with-feedback";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx, sortCx } from "@/utils/cx";

export interface InputBaseProps extends TextFieldProps {
	/** Tooltip message on hover. */
	tooltip?: string;
	/**
	 * Input size.
	 * @default "sm"
	 */
	size?: "sm" | "md";
	/** Placeholder text. */
	placeholder?: string;
	/** Class name for the icon. */
	iconClassName?: string;
	/** Class name for the input. */
	inputClassName?: string;
	/** Class name for the input wrapper. */
	wrapperClassName?: string;
	/** Class name for the tooltip. */
	tooltipClassName?: string;
	/** Keyboard shortcut to display. */
	shortcut?: string | boolean;
	/** HTML input type (e.g. number). */
	type?: AriaInputProps["type"];
	min?: number | string;
	max?: number | string;
	ref?: Ref<HTMLInputElement>;
	groupRef?: Ref<HTMLDivElement>;
	/** Icon component to display on the left side of the input. */
	icon?: ComponentType<HTMLAttributes<HTMLOrSVGElement>>;
}

export const InputBase = ({
	ref,
	tooltip,
	shortcut,
	groupRef,
	size = "sm",
	isInvalid,
	isDisabled,
	icon: Icon,
	placeholder,
	wrapperClassName,
	tooltipClassName,
	inputClassName,
	iconClassName,
	// Omit this prop to avoid invalid HTML attribute warning
	isRequired: _isRequired,
	...inputProps
}: Omit<InputBaseProps, "label" | "hint">) => {
	// Trailing slot: helper icon (tooltip) only — Figma does not auto-show an error icon
	const hasTrailingIcon = !!tooltip;
	const hasLeadingIcon = Icon;

	// If the input is inside a `TextFieldContext`, use its context to simplify applying styles
	const context = useContext(TextFieldContext);

	const inputSize = context?.size || size;

	// Figma Input (4341:78985): field h=36, content pad 12×6, icon 20×20 at x=12 with 8px gap to text.
	// sm = 36px (Button/IconButton sm + TableFilterBar); md = 40px.
	const sizes = sortCx({
		sm: {
			group: "h-9",
			root: cx(
				"h-full px-3",
				hasTrailingIcon && "pr-9",
				hasLeadingIcon && "pl-10",
			),
			iconLeading: "left-3 top-1/2 -translate-y-1/2",
			iconTrailing: "right-3 top-1/2 -translate-y-1/2",
			shortcut: "pr-2.5",
		},
		md: {
			group: "h-10",
			root: cx(
				"h-full px-3.5",
				hasTrailingIcon && "pr-9.5",
				hasLeadingIcon && "pl-10.5",
			),
			iconLeading: "left-3.5 top-1/2 -translate-y-1/2",
			iconTrailing: "right-3.5 top-1/2 -translate-y-1/2",
			shortcut: "pr-3",
		},
	});

	return (
		<AriaGroup
			{...{ isDisabled, isInvalid }}
			ref={groupRef}
			className={({ isFocusWithin, isDisabled, isInvalid }) =>
				cx(
					// Figma Input: radius-md, shadow-xs, 1px border-primary; focus → 2px border-brand_alt
					"relative flex w-full flex-row place-content-center place-items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary transition-shadow duration-100 ease-linear ring-inset",
					sizes[inputSize].group,

					isFocusWithin && !isDisabled && !isInvalid && "ring-2 ring-brand_alt",

					// Disabled state styles
					isDisabled && "cursor-not-allowed bg-disabled_subtle ring-disabled",
					"group-disabled:cursor-not-allowed group-disabled:bg-disabled_subtle group-disabled:ring-disabled",

					// Error → 2px border-error (Figma Error=True)
					isInvalid && "ring-2 ring-error",
					"group-invalid:ring-2 group-invalid:ring-error",

					context?.wrapperClassName,
					wrapperClassName,
				)
			}
		>
			{/* Leading icon and Payment icon */}
			{Icon && (
				<Icon
					className={cx(
						"pointer-events-none absolute size-5 text-fg-quaternary",
						isDisabled && "text-fg-disabled",
						sizes[inputSize].iconLeading,
						context?.iconClassName,
						iconClassName,
					)}
				/>
			)}

			{/* Input field */}
			<AriaInput
				{...(inputProps as AriaInputProps)}
				ref={ref}
				placeholder={placeholder}
				className={cx(
					"m-0 w-full cursor-text bg-transparent text-md leading-6 text-primary ring-0 outline-hidden placeholder:text-placeholder autofill:rounded-lg autofill:text-primary group-focus-within:font-medium",
					isDisabled && "cursor-not-allowed text-disabled",
					sizes[inputSize].root,
					context?.inputClassName,
					inputClassName,
				)}
			/>

			{/* Helper icon — Figma Helper icon (help-circle); not auto-shown on error */}
			{tooltip && (
				<Tooltip title={tooltip} placement="top">
					<TooltipTrigger
						className={cx(
							"absolute cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover",
							sizes[inputSize].iconTrailing,
							context?.tooltipClassName,
							tooltipClassName,
						)}
					>
						{isInvalid ? (
							<InfoCircle className="size-4 text-fg-error-secondary" />
						) : (
							<HelpCircle className="size-4" />
						)}
					</TooltipTrigger>
				</Tooltip>
			)}

			{/* Shortcut */}
			{shortcut && (
				<div
					className={cx(
						"pointer-events-none absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-bg-primary to-40% pl-8",
						sizes[inputSize].shortcut,
					)}
				>
					<span
						className={cx(
							"pointer-events-none rounded px-1 py-px text-xs font-medium text-quaternary ring-1 ring-secondary select-none ring-inset",
							isDisabled && "bg-transparent text-disabled",
						)}
						aria-hidden="true"
					>
						{typeof shortcut === "string" ? shortcut : "⌘K"}
					</span>
				</div>
			)}
		</AriaGroup>
	);
};

InputBase.displayName = "InputBase";

interface BaseProps {
	/** Label text for the input */
	label?: string;
	/** Helper text displayed below the input */
	hint?: ReactNode;
	/**
	 * Auto-save FeedbackIndicator in the label row (Figma Input Auto-save).
	 * @see FeedbackIndicator 4895:57620
	 */
	feedback?: DisplayFieldFeedbackType;
}

interface TextFieldProps
	extends BaseProps,
		AriaTextFieldProps,
		Pick<
			InputBaseProps,
			| "size"
			| "wrapperClassName"
			| "inputClassName"
			| "iconClassName"
			| "tooltipClassName"
		> {
	ref?: Ref<HTMLDivElement>;
}

const TextFieldContext = createContext<TextFieldProps>({});

export const TextField = ({ className, ...props }: TextFieldProps) => {
	return (
		<TextFieldContext.Provider value={props}>
			<AriaTextField
				{...props}
				data-input-wrapper
				className={(state) =>
					cx(
						// Figma Input stack gap = spacing-xs (4px)
						"group flex h-max w-full flex-col items-start justify-start gap-1",
						typeof className === "function" ? className(state) : className,
					)
				}
			/>
		</TextFieldContext.Provider>
	);
};

TextField.displayName = "TextField";

export interface InputProps extends InputBaseProps, BaseProps {
	/** Whether to hide required indicator from label */
	hideRequiredIndicator?: boolean;
}

export const Input = ({
	size = "sm",
	placeholder,
	icon: Icon,
	label,
	hint,
	feedback,
	shortcut,
	hideRequiredIndicator,
	className,
	ref,
	groupRef,
	tooltip,
	iconClassName,
	inputClassName,
	wrapperClassName,
	tooltipClassName,
	...props
}: InputProps) => {
	return (
		<TextField
			aria-label={!label ? placeholder : undefined}
			{...props}
			className={className}
		>
			{({ isRequired, isInvalid }) => (
				<>
					<LabelWithFeedback
						label={label}
						feedback={feedback}
						isRequired={
							hideRequiredIndicator ? !hideRequiredIndicator : isRequired
						}
						isFilled={isRequiredFieldFilled(props.value)}
					/>

					<InputBase
						{...{
							ref,
							groupRef,
							size,
							placeholder,
							icon: Icon,
							shortcut,
							iconClassName,
							inputClassName,
							wrapperClassName,
							tooltipClassName,
							tooltip,
						}}
					/>

					{hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
				</>
			)}
		</TextField>
	);
};

Input.displayName = "Input";
